/**
 * Single source of truth for the web app's route URLs.
 *
 * Two parallel representations:
 *   - `webPaths.*`     concrete-URL builders for <Link to> / navigate and for
 *                      API-emitted links (Mareczek chat).
 *   - `routePatterns.*` the `:param` templates for <Route path> definitions.
 *
 * A unit test asserts the two stay in sync. Anything that builds or matches a
 * web URL goes through here so links never drift from the route table.
 */
export const webPaths = {
	landing: (): string => "/",
	auth: (): string => "/auth",
	dashboard: (): string => "/dashboard",
	myTickets: (): string => "/tickets",
	profile: (): string => "/profile",
	settings: (): string => "/settings",
	newProperty: (): string => "/properties/new",
	property: (id: string): string => `/properties/${id}`,
	propertyProjects: (id: string): string => `/properties/${id}/projects`,
	propertyJournal: (id: string): string => `/properties/${id}/journal`,
	propertyTickets: (id: string): string => `/properties/${id}/tickets`,
	propertyEdit: (id: string): string => `/properties/${id}/edit`,
	propertyTransfer: (id: string): string => `/properties/${id}/transfer`,
	project: (propertyId: string, projectId: string): string =>
		`/properties/${propertyId}/projects/${projectId}`,
	projectTasks: (propertyId: string, projectId: string): string =>
		`/properties/${propertyId}/projects/${projectId}/tasks`,
	projectJournal: (propertyId: string, projectId: string): string =>
		`/properties/${propertyId}/projects/${projectId}/journal`,
	projectDocuments: (propertyId: string, projectId: string): string =>
		`/properties/${propertyId}/projects/${projectId}/documents`,
	propertyDocuments: (propertyId: string): string =>
		`/properties/${propertyId}/documents`,
	propertyBudget: (propertyId: string): string =>
		`/properties/${propertyId}/budget`,
	projectBudget: (propertyId: string, projectId: string): string =>
		`/properties/${propertyId}/projects/${projectId}/budget`,
	ticket: (propertyId: string, ticketId: string): string =>
		`/properties/${propertyId}/tickets/${ticketId}`,
	invitation: (token: string): string => `/invitations/${token}`,
}

export const routePatterns = {
	landing: "/",
	auth: "/auth",
	invitation: "/invitations/:token",
	dashboard: "/dashboard",
	myTickets: "/tickets",
	newProperty: "/properties/new",
	property: "/properties/:id",
	propertyProjects: "/properties/:id/projects",
	project: "/properties/:id/projects/:projectId",
	projectTasks: "/properties/:id/projects/:projectId/tasks",
	projectJournal: "/properties/:id/projects/:projectId/journal",
	projectDocuments: "/properties/:id/projects/:projectId/documents",
	propertyDocuments: "/properties/:id/documents",
	propertyBudget: "/properties/:id/budget",
	propertyTickets: "/properties/:id/tickets",
	ticket: "/properties/:id/tickets/:ticketId",
	propertyJournal: "/properties/:id/journal",
	propertyEdit: "/properties/:id/edit",
	propertyTransfer: "/properties/:id/transfer",
	profile: "/profile",
	settings: "/settings",
} as const

/**
 * True for root-relative in-app paths (`/...`). Scheme URLs and
 * scheme-relative (`//host`) URLs are external. Used by the chat markdown
 * renderer to pick React Router navigation vs. a new-tab anchor.
 */
export function isInternalPath(href: string): boolean {
	return href.startsWith("/") && !href.startsWith("//")
}
